'use memo';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import IconButton from '@/components/Button/IconButton';
import { DataTable } from '@/components/DataTable';
import { SimpleDatePicker } from '@/components/DatePicker/SimpleDatePicker';
import { ConfirmActionDialog } from '@/components/Dialog/ConfirmActionDialog';
import { FormItem } from '@/components/FormItem';
import { FillScreen, HStack, VStack } from '@/components/Layout';
import { TooltipWrapper } from '@/components/Tooltip/TooltipWrapper';
import { Text } from '@/components/Typography/Text';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/use-toast';
import { MOCK_ONLY_FEATURE_FLAG } from '@/constants/featureFlags';
import { handleApiError } from '@/lib/api/error';
import { orpc } from '@/lib/api/orpc';
import { featureFlags } from '@/lib/env';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { QUERY_PRESETS } from '@/lib/query';
import { findPastDataDataset } from '../-shared/member-output-fields';
import { useOutboundTaskTable } from './-hooks/useTable';
import { buildMockPastDataTaskId, usePastDataMockTaskStore } from './-store/pastDataMockTasks';
export const Route = createFileRoute('/_staff/settings/data-connector/outbound/data-pipeline/$id/_pipeline-detail/execution-results/')({
  component: OutboundExecutionResultsPage
});
function OutboundExecutionResultsPage() {
  const {
    id: pipelineId
  } = Route.useParams();
  const {
    t,
    i18n
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dataConnector.outboundDataPipeline.detail.executionResults'
  });
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const {
    table,
    displayState
  } = useOutboundTaskTable(pipelineId);

  // 商談デモ向けの見せ球。デモ用モックの標準フラグ MOCK_ONLY 有効時のみ「過去データ実行」
  // 導線と基準日モーダルを出す。OFF（prd 既定）では従来どおり即時実行のみ。
  const isOutputFilterEnabled = featureFlags.check(MOCK_ONLY_FEATURE_FLAG);
  // 「過去データ実行」導線の文言（デモ用のためセクション内で日英保持。非 ja は英語）。
  const isJa = i18n.language.startsWith('ja');
  const pastDataExecuteLabel = isJa ? '過去データ実行' : 'Run with past data';
  const moreActionsLabel = isJa ? 'その他の操作' : 'More actions';
  const [isExecuteDialogOpen, setIsExecuteDialogOpen] = useState(false);
  const [baseDate, setBaseDate] = useState<Date | undefined>(undefined);

  // 実行計画（運用モード / トリガーモード）で手動実行の可否を判定する（IN と同じ挙動）。
  const detailQuery = useQuery({
    ...orpc.outboundDataPipeline.get.queryOptions({
      input: {
        id: pipelineId
      }
    }),
    ...QUERY_PRESETS.detail()
  });
  const isPaused = detailQuery.data?.executionPlan?.operationMode === 'PAUSED';
  const isScheduled = detailQuery.data?.executionPlan?.triggerMode === 'SCHEDULE';
  const isExecuteDisabled = isPaused || isScheduled;
  const executeDisabledReason = isPaused ? t('executeDisabledPaused') : isScheduled ? t('executeDisabledScheduled') : undefined;
  const executeMutation = useMutation(orpc.outboundDataPipelineTask.executeManual.mutationOptions({
    onSuccess: () => {
      // バッチ起動〜Task 作成までラグがあり即時には一覧へ反映されないため、
      // 「実行を開始した・時間がかかる・画面は自動更新されない」ことをトーストで明示する。
      setIsExecuteDialogOpen(false);
      toast({
        title: t('executeStartedTitle'),
        description: t('executeStartedDescription')
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.outboundDataPipelineTask.listTasks.key({
          input: {
            pipelineId
          }
        })
      });
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      toast({
        description: message,
        variant: 'destructive'
      });
    }
  }));

  // 通常の「実行」は即時に手動実行する。
  const handleExecuteClick = () => {
    executeMutation.mutate({
      pipelineId
    });
  };

  // 「過去データ実行」は基準日を指定するモーダルを開く（商談デモ用の見せ球）。
  const handlePastDataExecuteClick = () => {
    setBaseDate(undefined);
    setIsExecuteDialogOpen(true);
  };
  const addMockPastDataTask = usePastDataMockTaskStore(state => state.addTask);

  // 過去データ実行（デモ）: 基準日に対応するダミーデータセットがあれば、実行結果一覧の先頭へ
  // 完了タスク行を 1 件追加する（クライアント保持のみ）。ダウンロード可能になるまでの「準備中」
  // 表示と該当日ダミー CSV の出力は、行の成果物セル（MockPastDataDownloadButton）が担う。
  // 対応日が無ければ「データなし」を通知する。
  const handleConfirmExecute = () => {
    setIsExecuteDialogOpen(false);
    const dataset = findPastDataDataset(baseDate);
    if (!dataset) {
      toast({
        description: isJa ? '選択した基準日のデータはありません。' : 'No data is available for the selected base date.'
      });
      return;
    }
    const now = new Date().toISOString();
    addMockPastDataTask(pipelineId, {
      taskId: buildMockPastDataTaskId(dataset.key, Date.now()),
      status: 'TASK_COMPLETED',
      statusLabel: isJa ? '完了' : 'Completed',
      recordCount: dataset.recordCount,
      errorCount: 0,
      startedAt: now,
      completedAt: now,
      downloadable: true,
      failureCode: null,
      failureMessage: null
    });
    toast({
      title: isJa ? '過去データ実行を開始しました' : 'Past-data run started',
      description: isJa ? '実行結果に追加しました。行のダウンロードから出力できます。' : 'Added to the execution results. Download it from the row.'
    });
  };
  return <FillScreen className="flex flex-col gap-4 px-12 py-5">
      <HStack className="justify-end gap-2">
        <TooltipWrapper content={executeDisabledReason}>
          <ActionButton variant="secondary" icon="RocketIcon" onClick={handleExecuteClick} isLoading={executeMutation.isPending} disabled={isExecuteDisabled}>
            {t('execute')}
          </ActionButton>
        </TooltipWrapper>
        {isOutputFilterEnabled && <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton icon="MoreHorizontalIcon" variant="ghost" aria-label={moreActionsLabel} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handlePastDataExecuteClick} disabled={isExecuteDisabled}>
                {pastDataExecuteLabel}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>}
      </HStack>
      <div className="min-h-0 flex-1 overflow-auto">
        <DataTable table={table} displayState={displayState} onRowClick={null} />
      </div>

      {isOutputFilterEnabled && <ConfirmActionDialog isOpen={isExecuteDialogOpen} onClose={() => setIsExecuteDialogOpen(false)} onConfirm={handleConfirmExecute} isLoading={executeMutation.isPending} title={pastDataExecuteLabel} okLabel={t('executeDialog.confirm')} description={<VStack className="gap-3 py-2">
              <Text variant="p">{t('executeDialog.description')}</Text>
              <FormItem fieldTitle={t('executeDialog.baseDate')} fieldInput={<SimpleDatePicker value={baseDate} onChange={setBaseDate} placeholder={t('executeDialog.baseDatePlaceholder')} allowEmpty className="w-full" />} />
            </VStack>} />}
    </FillScreen>;
}